/* eslint-disable react/no-danger */
import { useRef } from 'react';
import styled from 'styled-components';
import css from '@styled-system/css';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';

import { contentfulCustomerStoryType } from 'types';
import Text from 'legacy/components/text';
import ContentfulRichText from 'legacy/components/contentfulRichText';
import Flex from 'legacy/components/flex';
import Box from 'legacy/components/box';
import SocialSharingButtons from 'legacy/components/socialSharingButtons';
import CompanySnapshot from 'legacy/components/customerStory/companySnapshot';

const TableWrapper = styled(Box)(
  css({
    ml: [0, null, null, null, '-20%'],
    width: ['100%', null, null, null, '140%'],
  })
);

const StyledSharingButtons = styled(SocialSharingButtons)(
  css({
    justifyContent: 'flex-start',
    maxWidth: ['100%', null, null, null, '37rem'],
    ml: ['-1.5rem', null, null, null, '5rem'],
  })
);

const StyledText = styled(Text)(
  css({
    lineHeight: 2,
    '& > b': {
      fontWeight: 600,
    },
  })
);

const StoryContainer = styled(Box)(
  css({
    width: '100%',
  })
);

const StoryWrapper = styled(Box)(
  css({
    maxWidth: [null, '100%', null, '50rem'],
    ml: [null, 24, null, 'auto'],
    mr: [null, 24, null, 'auto'],
    px: ['1.5rem', null, 0],
  })
);

const StoryBody = styled(Box)(
  css({
    position: 'relative',
    mt: [5, null, 6, 7],
    '> p, > h1, > h2, > h3, > h4, > h5, > h6': {
      maxWidth: ['100%', null, null, null, '37rem'],
      mx: 'auto',
    },
    '& table': {
      display: 'block',
      maxWidth: 'fit-content',
      overflowX: 'auto',
      mx: [0, null, null, null, 'auto'],
      width: ['100%', null, null, null, '140%'],
      borderRadius: 'default',
      borderSpacing: 0,
      px: 2,
      pb: 2,
      bg: 'blue100',
      fontFamily: 'body',
      color: 'text',
      '& b, & strong': {
        fontWeight: 700,
        color: 'blue',
      },
      '& highlight': {
        fontWeight: 700,
        color: 'teal40',
      },
      '& th': {
        p: 3,
        fontWeight: 400,
        textAlign: 'left',
      },
      '& td': {
        py: 2,
        px: 3,
      },
      '& thead': {
        '& tr': {
          bg: 'inherit',
        },
      },
      '& tbody': {
        '& tr': {
          '&:nth-of-type(2n+1)': {
            bg: 'white',
          },
          '&:nth-of-type(2n)': {
            bg: 'inherit',
          },
        },
      },
    },
    '& img': {
      display: 'block',
      m: 'auto',
    },
  })
);

const StyledStory = styled.article(
  css({
    mx: 'auto',
    maxWidth: '100%',
    borderRadius: 'large',
    bg: 'white',
    width: '100%',
  })
);

const renderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <StyledText mt={3}>{children}</StyledText>,
  },
  renderMark: {
    [MARKS.CODE]: (text) =>
      text.includes('table') ? (
        <TableWrapper dangerouslySetInnerHTML={{ __html: text }} />
      ) : (
        <span dangerouslySetInnerHTML={{ __html: text }} />
      ),
  },
};

const Story = ({ story }) => {
  const storyRef = useRef(null);

  const companyInfo = {
    industry: story.industry,
    size: story.companySize,
    location: story.location,
    payroll: story.payroll,
    solution: story.solution,
  };

  return (
    <Flex width={1} maxWidth="120rem" mx="auto">
      <StoryContainer ref={storyRef}>
        <StoryWrapper>
          <StyledStory>
            <StoryBody>
              <CompanySnapshot snapshot={companyInfo} />
              <Box mt={4}>
                <Text color="blue" fontWeight={700}>
                  Share on
                </Text>
                <StyledSharingButtons
                  socialSharingOptions={story.socialSharingOptions}
                  alignment="left"
                />
              </Box>
              <ContentfulRichText
                content={story.body?.json}
                links={story.body?.links}
                renderOptions={renderOptions}
              />
            </StoryBody>
          </StyledStory>
        </StoryWrapper>
      </StoryContainer>
    </Flex>
  );
};

Story.propTypes = {
  story: contentfulCustomerStoryType.isRequired,
};

export default Story;
